import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { Sparkles, Calendar, CalendarPlus, BookOpenText, BarChart3, IndianRupee, Users, Wheat, ShieldCheck, ArrowRight, ChevronRight, Cookie, Calculator as CalcIcon, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/mdmData";
import { findAvatar } from "../lib/personalize";
import PromotionalAd from "../components/PromotionalAd";

export default function Dashboard() {
  const nav = useNavigate();
  const { user, loading, isAuthed } = useAuth();
  const [sub, setSub] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [recent, setRecent] = useState([]);

  const monthKey = format(new Date(), "yyyy-MM");
  const monthLabel = format(new Date(), "MMMM yyyy");
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  useEffect(() => {
    if (!loading && !isAuthed) nav("/login");
  }, [loading, isAuthed, nav]);

  useEffect(() => {
    if (!isAuthed) return;
    api.subMe().then(setSub).catch(() => {});
    api.dailySummary(monthKey).then(setMonthSummary).catch(() => {});
    api.listDailyEntries({ limit: 5 }).then(setRecent).catch(() => {});
  }, [isAuthed, monthKey]);

  if (loading || !user) return <div className="p-10 text-center text-slate-500" data-testid="dashboard-loading">Loading your dashboard…</div>;

  const premium = !!sub?.is_premium;
  const expiryStr = sub?.subscription?.expiry_date ? new Date(sub.subscription.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : null;
  const firstName = user.full_name?.split(" ")[0] || "teacher";
  const avatar = findAvatar(user.avatar_id);
  // Recovery number is hashed on the server — the client no longer knows it,
  // so we simply show the chip with the avatar. Dashboard doesn't need it.

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="dashboard-page">
      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="chip chip-green"><Sparkles size={14}/> Dashboard</div>
          {user.personalized ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-50 to-amber-50 border border-emerald-200 pl-1.5 pr-3 py-1 shadow-sm" data-testid="dashboard-personal-chip">
              <span className={`h-8 w-8 rounded-full grid place-items-center text-xl ${avatar?.iconBg || "bg-emerald-100"}`} aria-hidden>
                {avatar?.emoji || "🙋"}
              </span>
              <span className="text-sm font-semibold text-slate-800">
                Namaste, <span className="text-emerald-800">{firstName}</span>
              </span>
              {avatar && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800">
                  · {avatar.label || "Recovery set"}
                </span>
              )}
            </div>
          ) : null}
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
            {greeting}, {firstName} 👋
          </h1>
          <p className="mt-1 text-slate-600">
            {user.school_name
              ? user.school_name
              : (user.personalized
                  ? "Add your school details in Profile to personalize reports."
                  : <>Finish your setup — <Link to="/personalize" className="text-emerald-700 font-semibold hover:underline" data-testid="dashboard-personalize-link">pick your recovery number & image</Link>.</>)}
          </p>
        </div>
        {premium ? (
          <div className="chip chip-green" data-testid="dashboard-plan-badge"><ShieldCheck size={12}/> Premium · valid until {expiryStr}</div>
        ) : (
          <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white h-11" data-testid="dashboard-upgrade-btn">
            <Link to="/premium"><Sparkles size={16}/> Unlock Premium — ₹299/year</Link>
          </Button>
        )}
      </div>

      {/* Refer & Earn card */}
      <Link to="/referrals" className="block rounded-2xl border-2 border-amber-200 bg-gradient-to-r from-amber-50 via-emerald-50 to-amber-50 p-4 sm:p-5 hover:shadow-md transition-all" data-testid="dashboard-refer-card">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0"><Gift size={20}/></span>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-amber-900 text-base sm:text-lg">🎁 Refer &amp; Earn Premium FREE!</div>
            <p className="text-xs sm:text-sm text-amber-900/80 truncate">50 successful Premium referrals = 1 YEAR PREMIUM FREE 🎉</p>
          </div>
          <ArrowRight size={20} className="text-amber-800 shrink-0"/>
        </div>
      </Link>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={<Calendar size={16}/>} label={`Days · ${monthLabel}`} value={monthSummary?.days_recorded ?? 0} tint="slate" testid="dashboard-kpi-days"/>
        <Kpi icon={<Users size={16}/>} label="Meals served" value={monthSummary?.total_students ?? 0} tint="emerald" testid="dashboard-kpi-meals"/>
        <Kpi icon={<IndianRupee size={16}/>} label="Total cost" value={formatCurrency(monthSummary?.total_cost || 0)} tint="orange" testid="dashboard-kpi-cost"/>
        <Kpi icon={<Wheat size={16}/>} label="Rice" value={`${(monthSummary?.total_rice_kg || 0).toFixed(2)} kg`} tint="emerald" testid="dashboard-kpi-rice"/>
      </div>

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dashboard-quick-actions">
        <QuickAction to="/daily-entry" icon={<CalendarPlus size={18}/>} title="Save today's entry" desc="Enter attendance, we calculate and save." premium={!premium}/>
        <QuickAction to="/daily-history" icon={<BookOpenText size={18}/>} title="Daily history" desc="Every day you've served." />
        <QuickAction to="/calculator" icon={<CalcIcon size={18}/>} title="Open calculator" desc="Public tool — no save."/>
        <QuickAction to="/laddoo" icon={<Cookie size={18}/>} title="Ladoo calculator" desc="Tue / Thu / Sat servings."/>
        <QuickAction to="/poshan-panji" icon={<BookOpenText size={18}/>} title="Poshan Panji" desc="By UDISE (public register)."/>
        <QuickAction to="/reports" icon={<BarChart3 size={18}/>} title="Reports" desc="Weekly / Monthly / Annual." premium={!premium}/>
      </div>

      {/* Recent */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="dashboard-recent">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="chip chip-slate">📒 Recent entries</div>
            <div className="mt-1 font-display font-bold text-slate-900">Last 5 saved days</div>
          </div>
          <Button variant="ghost" asChild data-testid="dashboard-recent-all">
            <Link to="/daily-history">See all <ChevronRight size={14}/></Link>
          </Button>
        </div>
        {!recent.length && (
          <div className="p-8 text-center text-slate-500" data-testid="dashboard-recent-empty">
            {premium ? "No entries yet — save today's report to start your register." : (
              <>
                Save daily entries to see them here. <Link to="/premium" className="text-emerald-700 font-semibold hover:underline">Unlock Premium →</Link>
              </>
            )}
          </div>
        )}
        {recent.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {recent.map((e) => (
              <li key={e.id} className="p-4 flex items-center gap-3" data-testid={`dashboard-recent-${e.id}`}>
                <div className="h-10 w-10 rounded-lg bg-slate-100 grid place-items-center text-slate-600 shrink-0">
                  {e.entry_type === "ladoo" ? "🧁" : "🍛"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm">{format(new Date(e.date_iso), "d MMM yyyy")} · {e.weekday_name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.menu_display}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-bold text-slate-900">{formatCurrency(e.summary?.grand_total || 0)}</div>
                  <div className="text-xs text-slate-500">{e.summary?.total_students || 0} students</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Native ad — dashboard footer */}
      <PromotionalAd placement="dashboard" variant="horizontal"/>
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" : tint === "orange" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-2xl text-slate-900">{value}</div>
    </div>
  );
}

function QuickAction({ to, icon, title, desc, premium }) {
  const nav = useNavigate();
  const go = () => {
    if (premium) {
      toast("This is a Premium feature — unlocking checkout…");
      return nav("/premium");
    }
    nav(to);
  };
  return (
    <button onClick={go}
            className={`group text-left rounded-2xl border p-4 transition-all hover:shadow-md ${premium ? "border-orange-200 bg-orange-50/40" : "border-slate-200 bg-white hover:border-emerald-300"}`}
            data-testid={`quick-${to.replace("/", "")}`}>
      <div className="flex items-start gap-3">
        <span className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${premium ? "bg-orange-500 text-white" : "bg-emerald-700 text-white"}`}>{icon}</span>
        <div className="flex-1">
          <div className="font-display font-bold text-slate-900 flex items-center gap-2">{title} {premium && <span className="chip chip-saffron text-[10px]">Premium</span>}</div>
          <div className="text-sm text-slate-600">{desc}</div>
        </div>
        <ArrowRight size={16} className="text-slate-400 group-hover:text-emerald-700 mt-1"/>
      </div>
    </button>
  );
}
